import React, { useCallback, useEffect, useState } from "react";
import style from "./Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getMonetizationPayouts,
  generatePayouts,
  // approvePayout,
  markPayoutPaid,
  payPayoutViaStripe,
  exportPayoutsCsv,
  exportPayoutLedgerXlsx,
  runRevenueEngine,
} from "../../api/HttpApis";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const now = new Date();
const CUR_YEAR = now.getUTCFullYear();
const CUR_MONTH = now.getUTCMonth() + 1;
const isoDate = (d) => d.toISOString().slice(0, 10);
// Default ledger range: current month to date. The backend itself still
// defaults to a trailing 12 months when start/end are omitted — this only
// changes what the date pickers are pre-filled with; an admin can still
// widen the range manually, and exportPayoutLedgerXlsx always sends both
// dates explicitly once a value is picked here.
const DEFAULT_LEDGER_START = isoDate(new Date(Date.UTC(CUR_YEAR, now.getUTCMonth(), 1)));
const DEFAULT_LEDGER_END = isoDate(now);

const STATUSES = ["", "holding", "pending", "approved", "paid", "on_hold", "failed", "processing", "reversed"];
const STATUS_LABELS = { holding: "Held" };
const chipClass = (s) =>
  ({
    pending: style.chipPending,
    approved: style.chipApproved,
    paid: style.chipPaid,
    on_hold: style.chipOn_hold,
    failed: style.chipFailed,
    holding: style.chipHolding,
    processing: style.chipApproved,
    reversed: style.chipFailed,
  }[s] || style.chipPending);

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [year, setYear] = useState(CUR_YEAR);
  const [month, setMonth] = useState(CUR_MONTH);
  const [busy, setBusy] = useState(false);
  // Which of the two top-level actions is in flight, so only that button shows
  // a spinner (both stay disabled via `busy` while either one runs).
  const [runningAction, setRunningAction] = useState(null); // "engine" | "generate" | null
  const [ledgerStart, setLedgerStart] = useState(DEFAULT_LEDGER_START);
  const [ledgerEnd, setLedgerEnd] = useState(DEFAULT_LEDGER_END);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const limit = 50;

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMonetizationPayouts({ page, limit, status, year, month });
      // Backend envelope: { status, data: { data: [...], total } }
      const payload = res?.data ?? res ?? {};
      setPayouts(Array.isArray(payload.data) ? payload.data : []);
      setTotal(payload.total || 0);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load payouts", "error");
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, [page, status, year, month]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleRunEngine = async () => {
    if (!window.confirm(`Run the revenue engine for ${year}-${String(month).padStart(2, "0")}? This recalculates earnings.`))
      return;
    setBusy(true);
    setRunningAction("engine");
    try {
      const res = await runRevenueEngine({ year, month });
      // Backend envelope: { status, message, data: { ...run } }. BigInt fields
      // are serialized as strings, so coerce with Number().
      const run = res?.data ?? res ?? {};
      const affiliate = Number(run.affiliate_total_cents || 0);
      const pool = Number(run.watch_pool_cents || 0);
      const net = Number(run.net_subscription_cents || 0);
      const total = affiliate + pool;
      showToast(
        `Revenue run complete — ${money(total)} to ${run.creators_paid || 0} creator(s) ` +
          `(affiliate ${money(affiliate)} + watch pool ${money(pool)}; net revenue ${money(net)}).`,
        "success"
      );
      fetchPayouts();
    } catch (err) {
      showToast(err?.response?.data?.message || "Revenue run failed", "error");
    } finally {
      setBusy(false);
      setRunningAction(null);
    }
  };

  // True when the selected period has not finished yet (current or future month).
  // Generating payouts before a period closes freezes amounts that later change
  // as more sales / watch-time arrive — so we warn before doing it.
  const periodNotOver = year > CUR_YEAR || (year === CUR_YEAR && month >= CUR_MONTH);

  const handleGenerate = async () => {
    const periodLabel = `${year}-${String(month).padStart(2, "0")}`;
    const warning = periodNotOver
      ? `Heads up: ${periodLabel} is not over yet. Earnings can still change as more sales and watch-time come in, so payouts generated now may need regenerating later. Generate anyway?`
      : `Generate payouts for ${periodLabel}?`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    setRunningAction("generate");
    try {
      const res = await generatePayouts({ year, month });
      // Backend envelope: { status, message, data: { created, updated, skipped } }.
      const out = res?.data ?? res ?? {};
      const created = out?.created ?? 0;
      const updated = Array.isArray(out?.updated) ? out.updated.length : 0;
      const existed = Array.isArray(out?.skipped)
        ? out.skipped.filter((s) => s.reason === "exists").length
        : 0;
      const outOfSync = Array.isArray(out?.skipped)
        ? out.skipped.filter((s) => s.reason === "out_of_sync").length
        : 0;

      const parts = [`${created} new`];
      if (updated) parts.push(`${updated} updated`);
      if (existed) parts.push(`${existed} already up to date`);
      showToast(`Payouts: ${parts.join(", ")}.`, "success");

      if (outOfSync) {
        showToast(
          `${outOfSync} payout(s) couldn't be updated because they're already paid/in-progress but earnings changed. Reconcile manually.`,
          "error"
        );
      }
      fetchPayouts();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not generate payouts", "error");
    } finally {
      setBusy(false);
      setRunningAction(null);
    }
  };

  const handleAction = async (fn, id, successMsg, extra) => {
    setBusy(true);
    try {
      await fn(id, extra);
      showToast(successMsg, "success");
      fetchPayouts();
    } catch (err) {
      showToast(err?.response?.data?.message || "Action failed", "error");
    } finally {
      setBusy(false);
    }
  };

  // Sending money via Stripe is irreversible — require an explicit, amount-aware
  // confirmation that names the creator before the transfer fires.
  const handlePayStripe = (p) => {
    const creatorName = p.creator?.name || p.creator?.email || `creator #${p.creator?.id}`;
    const confirmed = window.confirm(
      `Send ${money(p.amount_cents)} to ${creatorName} via Stripe now?\n\n` +
        `This transfers real money and cannot be undone. The creator must have completed Stripe payout onboarding.`
    );
    if (!confirmed) return;
    handleAction(payPayoutViaStripe, p.id, `Sent ${money(p.amount_cents)} to ${creatorName} via Stripe`);
  };

  // const handleApprove = (p) => {
  //   const creatorName = p.creator?.name || p.creator?.email || `creator #${p.creator?.id}`;
  //   if (!window.confirm(`Approve the ${money(p.amount_cents)} payout for ${creatorName}? (No money moves yet.)`)) return;
  //   handleAction(approvePayout, p.id, "Payout approved");
  // };

  const handleMarkPaid = (p) => {
    const creatorName = p.creator?.name || p.creator?.email || `creator #${p.creator?.id}`;
    if (
      !window.confirm(
        `Mark the ${money(p.amount_cents)} payout for ${creatorName} as PAID outside the system?\n\n` +
          `Use this only if you already paid them by other means (bank, PayPal, etc.). It does NOT send money.`
      )
    )
      return;
    const reference = window.prompt("Payment reference (optional):", "");
    if (reference === null) return; // cancelled
    handleAction(markPayoutPaid, p.id, "Marked as paid", { reference });
  };

  const handleExport = async () => {
    try {
      await exportPayoutsCsv({ year, month, status });
      showToast("Export started", "success");
    } catch (err) {
      showToast("Export failed", "error");
    }
  };

  const handleExportLedger = async () => {
    setLedgerBusy(true);
    try {
      await exportPayoutLedgerXlsx({ start: ledgerStart, end: ledgerEnd });
      showToast("Ledger export started", "success");
    } catch (err) {
      showToast("Ledger export failed", "error");
    } finally {
      setLedgerBusy(false);
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / limit));
  const years = [CUR_YEAR, CUR_YEAR - 1, CUR_YEAR - 2];

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>Payouts</h2>
          <p className={style.subtitle}>Run the monthly revenue engine, generate payouts, and pay creators.</p>
        </div>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={handleExport}>
          Download Report (CSV)
        </button>
      </div>

      <div className={style.toolbar}>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Ledger export:</span>
        <input
          type="date"
          className={style.select}
          style={{ width: 140 }}
          value={ledgerStart}
          max={ledgerEnd}
          onChange={(e) => setLedgerStart(e.target.value)}
        />
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>to</span>
        <input
          type="date"
          className={style.select}
          style={{ width: 140 }}
          value={ledgerEnd}
          min={ledgerStart}
          onChange={(e) => setLedgerEnd(e.target.value)}
        />
        <button
          className={`${style.btn} ${style.btnGhost}`}
          onClick={handleExportLedger}
          disabled={ledgerBusy}
          title="Full transaction-level ledger across every creator: Summary, Watch Pool, Affiliate, TVOD, and Payout Status, each on its own sheet."
        >
          {ledgerBusy ? "Preparing…" : "Export Ledger (.xlsx)"}
        </button>
      </div>

      <div className={style.toolbar}>
        <select className={style.select} style={{ width: 110 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select className={style.select} style={{ width: 130 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}
            </option>
          ))}
        </select>
        <select className={style.select} style={{ width: 140 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === "" ? "All statuses" : STATUS_LABELS[s] || s.replace("_", " ")}
            </option>
          ))}
        </select>
        <button className={style.btn} onClick={handleRunEngine} disabled={busy} title="Step 1: recalculate creator earnings for the selected period">
          {runningAction === "engine" && <span className={style.btnSpinner} />}
          1. Run Revenue Engine
        </button>
        <button className={style.btn} onClick={handleGenerate} disabled={busy} title="Step 2: create payout records from the latest earnings (run after the period ends)">
          {runningAction === "generate" && <span className={style.btnSpinner} />}
          2. Generate Payouts
        </button>
      </div>

      <div className={style.tableWrap}>
        <div className={style.tableScroll}>
        <table className={style.table}>
          <thead>
            <tr>
              <th>Creator</th>
              <th>Period</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Paid At</th>
              <th>Reference</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className={style.loading}>Loading...</td>
              </tr>
            ) : payouts.length === 0 ? (
              <tr>
                <td colSpan={7} className={style.empty}>
                  No payouts yet. Run the revenue engine, then Generate Payouts.
                </td>
              </tr>
            ) : (
              payouts.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.creator?.name}
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>{p.creator?.email}</div>
                  </td>
                  <td>{p.period}</td>
                  <td>{money(p.amount_cents)}</td>
                  <td>
                    <span className={`${style.chip} ${chipClass(p.status)}`}>
                      {STATUS_LABELS[p.status] || (p.status || "").replace("_", " ")}
                    </span>
                    {/* A released payout can absorb several months. Showing the
                        range it covers is what tells an admin this $X is for
                        Jan–Mar, not just this month — without it, two rows
                        covering overlapping periods look identical. */}
                    {p.covers_from && p.covers_from !== p.period && (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                        covers {p.covers_from} → {p.period}
                      </div>
                    )}
                    {p.status === "holding" && p.hold_release_at && (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                        releases {new Date(p.hold_release_at).toLocaleDateString()}
                      </div>
                    )}
                    {p.status === "paid" && p.confirmation_email_status === "failed" && (
                      <div
                        style={{ fontSize: 11, color: "#f59e0b", marginTop: 2 }}
                        title={p.confirmation_email_error || "The creator was paid, but the receipt email could not be sent."}
                      >
                        ⚠ receipt email failed
                      </div>
                    )}
                  </td>
                  <td>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                  <td style={{ fontSize: 12 }}>{p.paid_reference || "—"}</td>
                  <td>
                    <div className={style.rowActions}>
                      {p.status === "holding" && (
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }} title="Earned, but not yet payable — see the status column for why.">
                          not yet payable
                        </span>
                      )}
                      {/* {p.status === "pending" && (
                        <button
                          className={`${style.btn} ${style.btnGhost} ${style.btnSm}`}
                          onClick={() => handleApprove(p)}
                          disabled={busy}
                          title="Mark this payout as reviewed & approved. No money moves — it's just an authorization step before paying."
                        >
                          Approve
                        </button>
                      )} */}
                      {p.status !== "paid" && p.status !== "holding" && (
                        <>
                          <button
                            className={`${style.btn} ${style.btnSm}`}
                            onClick={() => handlePayStripe(p)}
                            disabled={busy}
                            title="⚠️ Sends REAL money: transfers this amount to the creator's Stripe account and marks it paid. Requires the creator to have completed Stripe payout onboarding."
                          >
                            Pay (Stripe)
                          </button>
                          <button
                            className={`${style.btn} ${style.btnGhost} ${style.btnSm}`}
                            onClick={() => handleMarkPaid(p)}
                            disabled={busy}
                            title="Record that you paid this creator OUTSIDE the system (bank transfer, PayPal, etc.). No money moves through the app — it only marks the payout as paid with your reference note."
                          >
                            Mark Paid
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {total > limit && (
        <div className={style.toolbar} style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span style={{ alignSelf: "center", fontSize: 13 }}>
            Page {page} of {lastPage}
          </span>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
